import {
  Alert,
  Button,
  Grid,
  Heading,
  HStack,
  Image,
  Link,
  Text
} from '@chakra-ui/react'
import { useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { Link as RouterLink } from 'react-router'
import { Lock } from '../icons'

import { InputFormControl, SwitchFormControl } from 'BrokerTsLib/src/components'
import { BadRequestError, type ModelState } from 'BrokerTsLib/src/dtos'
import type { LoginRequest } from 'BrokerTsLib/src/dtos/Account/LoginRequest'
import { useLogin } from 'BrokerTsLib/src/hooks/useAccount'
import { useReturnUrlNavigate } from 'BrokerTsLib/src/hooks/useReturnUrlNavigate'
import { setModelStateErrors } from 'BrokerTsLib/src/Utils/formModelState'
import * as yup from 'yup'
import okAppkyLogo from '../assets/images/logo-ok-appky.svg'
import { Card, CardBody } from '../components/Card'
import { SSOLoginButtons } from './SSOLoginButtons'

const loginSchema = yup.object().shape({
  userName: yup.string().required('Uživatelské jméno je povinné'),
  password: yup.string().required('Heslo je povinné'),
  rememberMe: yup.boolean()
})

export const LoginForm = ({ returnUrl: returnUrlParams, error }: { returnUrl?: string, error?: boolean }) => {
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setError
  } = useForm<LoginRequest>({
    defaultValues: {
      userName: '',
      password: '',
      rememberMe: false,
    },
    resolver: yupResolver(loginSchema) as any,
    mode: 'onSubmit'
  })

  const returnUrlNavigate = useReturnUrlNavigate()
  const { mutateAsync: loginAsync } = useLogin()
  const onSubmit = async (values: LoginRequest) => {
    try {
      const response = await loginAsync(values)
      const returnUrl = response?.returnUrl ?? returnUrlParams ?? '/'
      returnUrlNavigate(returnUrl)
    } catch (e: any) {
      // Handle 400 Bad Request errors (validation errors from server)
      if (e?.response?.status === 400 && e?.response?.data) {
        setModelStateErrors(setError, new BadRequestError(e.response.data as ModelState<LoginRequest>))
      }
      // Handle 401 Unauthorized errors
      else if (e?.response?.status === 401) {
        setError('root', {
          type: 'manual',
          message: 'Nesprávné uživatelské jméno nebo heslo'
        })
      }
      // Handle network errors
      else if (!e?.response) {
        setError('root', {
          type: 'manual',
          message: 'Chyba připojení k serveru. Zkuste to prosím později.'
        })
      }
      // Handle other errors
      else {
        setError('root', {
          type: 'manual',
          message: 'Došlo k neočekávané chybě. Zkuste to prosím znovu.'
        })
      }
    }
  }

  return (
    <Grid sx={{ maxW: 'loginFormMaxW', m: 'auto', gap: 8, alignContent: 'start' }}>
      <Image src={okAppkyLogo} w={'auto'} maxW="14rem" h={12} m="auto" alt="OK appky" />

      <Card>
        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid sx={{ gap: 8 }}>
              <Heading as="h2" size="md">
                Přihlášení přes uživatelský účet
              </Heading>

              <Grid sx={{ gap: 4 }}>
                <InputFormControl name='userName' control={control}
                  label='Uživatelské jméno'
                  placeholder='Vaše uživatelské jméno'
                  autoComplete="username"
                  formControlVariant="fullWidth"
                  isRequired
                  tabIndex={1} />

                <InputFormControl name='password' control={control}
                  labelProps={{ w: 'full' }}
                  label={<HStack w="full" justifyContent="space-between">
                    <Text >Heslo</Text>
                    <Link as={RouterLink} sx={{ fontSize: 'sm' }} to="/auth/reset-password" position='absolute' right={0} tabIndex={5} >
                      Obnovit heslo
                    </Link>
                  </HStack>}
                  formControlVariant="fullWidth"
                  type="password"
                  placeholder="Vaše přístupové heslo"
                  autoComplete="password"
                  isRequired
                  tabIndex={2} />
              </Grid>
              <Grid
                sx={{
                  width: '100%',
                  gridTemplateColumns: 'auto auto',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Button colorScheme="primary" isLoading={isSubmitting} type="submit" tabIndex={3}>
                  <Lock />
                  Přihlásit se
                </Button>

                <SwitchFormControl name='rememberMe' control={control} formControlVariant="inline" label="Zapamatovat heslo" size="sm" tabIndex={4} />
              </Grid>
              {errors.root && <Alert status='error'>{errors.root.message}</Alert>}
            </Grid>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <Grid sx={{ gap: 4 }}>
            <Heading as="h2" size="md">
              Centrální přihlášení
            </Heading>

            <SSOLoginButtons returnUrl={returnUrlParams} />

            <Text sx={{ fontSize: 'xs', color: 'gray.500' }}>
              Budete přesměrováni na stránku pro přihlášení přes SSO.
            </Text>
          </Grid>
        </CardBody>
      </Card>
    </Grid>
  )
}
